import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { isEmpty } from "@/utils/query-string";
import {
  Card,
  ComboboxInputValueChangeDetails,
  Flex,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { handleCreateFinancialMovementsDescriptionHandler } from "../schema/handler-functions";
import { AppColors } from "@/styles/colors";
import { InputField } from "@/components/input-field/InputField";
import { ComboboxComponent } from "@/components/select/Combobox";
import { getExistingFinancialMovementDesc } from "@/services/financial-movement-desc.service";
import { onlyNumbers } from "@/utils/reg-expressions";
import { useRoles } from "@/hooks/useRoles";

export const FinancialMovementsDescCreationPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const navigate = useNavigate();

  const { control } = useForm();

  const handleCancel = () => {
    navigate(AppRoutes.FINANCIAL_MOVEMENTS_DESC);
  };

  const [
    loadingCreateFinancialMovementsDesc,
    setLoadingCreateFinancialMovementsDesc,
  ] = useState<boolean>(false);

  const watchedValues = useWatch({
    control,
  });

  const [loadingExistingDescriptions, setLoadingExistingDescriptions] =
    useState<boolean>(false);

  const [existingDescriptions, setExistingDescriptions] = useState<string[]>(
    [],
  );

  const fetchExistingDescriptions = async () => {
    setLoadingExistingDescriptions(true);
    try {
      const response = await getExistingFinancialMovementDesc();

      setExistingDescriptions(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingExistingDescriptions(false);
    }
  };

  useEffect(() => {
    if (isActive) fetchExistingDescriptions();
  }, [isActive]);

  const [descriptionValue, setDescriptionValue] = useState<string>();

  const handleDescriptionChange = (e: ComboboxInputValueChangeDetails) => {
    setDescriptionValue(e.inputValue);
  };

  const hasNoFilledValues = useMemo(() => {
    const { code } = watchedValues || {};

    return isEmpty(descriptionValue) || isEmpty(code);
  }, [watchedValues, descriptionValue]);

  const { fetchPermissions } = useRoles();

  const handleCreateFinancialMovementDescription = async () => {
    handleCreateFinancialMovementsDescriptionHandler({
      setLoadingCreateFinancialMovementsDesc,
      navigate,
      watchedValues: {
        nombre: descriptionValue,
        codigo: watchedValues?.code,
      },
      fetchPermissions
    });
  };
  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar relación comprobante"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateFinancialMovementDescription}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loadingCreateFinancialMovementsDesc}
        loading={loadingCreateFinancialMovementsDesc}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(3, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <InputField
                inputName="code"
                labelText="Código"
                control={control}
                disabled={loadingCreateFinancialMovementsDesc}
                placeholder="Ingresar código"
                allowedPattern={onlyNumbers}
              />
            </GridItem>
            <GridItem paddingX="16px" paddingY="8px">
              {Boolean(
                !loadingExistingDescriptions && existingDescriptions?.length,
              ) && (
                <ComboboxComponent
                  control={control}
                  name="name"
                  label="Nomenclatura"
                  inputValue={descriptionValue}
                  items={existingDescriptions.map((el: string) => ({
                    value: el,
                    label: el,
                  }))}
                  placeholder="Ingresar nomenclatura"
                  disabled={
                    loadingCreateFinancialMovementsDesc ||
                    loadingExistingDescriptions
                  }
                  loading={loadingExistingDescriptions}
                  onChange={handleDescriptionChange}
                  clearOutside={false}
                  noResultText="Creará una nueva nomenclatura"
                  menuMaxHeight={{
                    xs: "350px",
                    sm: "420px",
                    md: "500px",
                    lg: "500px",
                    xl: "600px",
                  }}
                />
              )}
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>
    </Flex>
  );
};
